// File: src/components/admin/UserDetail/UserAIAnalystTab.js
// Per-user AI Analyst tab for admin user detail view (Issue #71)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Grid, Card, CardContent, Chip, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, LinearProgress, Alert, IconButton,
    Tooltip, Collapse, CircularProgress, Button, Select, MenuItem, FormControl,
    InputLabel, List, ListItem, ListItemIcon, ListItemText, Divider, Stack
} from '@mui/material';
import {
    Block, Refresh, PlayArrow,
    Email, Notifications, ThumbUp, ThumbDown, KeyboardArrowDown, KeyboardArrowUp,
    Message, Recommend, MarkEmailRead, MarkEmailUnread
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';

// -- Helper Components --

const StatusChip = ({ status }) => {
    const config = {
        COMPLETED: { color: 'success', label: 'Completed' },
        FAILED: { color: 'error', label: 'Failed' },
        SKIPPED: { color: 'default', label: 'Skipped' },
    };
    const { color, label } = config[status] || { color: 'default', label: status };
    return <Chip size="small" color={color} label={label} />;
};

const EligibleChip = ({ eligible }) => (
    <Chip
        size="small"
        color={eligible ? 'success' : 'error'}
        label={eligible ? 'Yes' : 'No'}
        variant={eligible ? 'filled' : 'outlined'}
    />
);

const formatDuration = (ms) => {
    if (ms == null) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
};

const formatRelativeTime = (isoStr) => {
    if (!isoStr) return '-';
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
};

const formatDateTime = (isoStr) => {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleString();
};

const formatReasons = (reasons) => {
    const labels = {
        master_switch_disabled: 'Master switch off',
        task_paused: 'Task paused',
        user_opted_out: 'User opted out',
        subscription_plan_mismatch: 'Plan mismatch',
        audience_rules_not_met: 'Audience rules not met',
        ai_insights_disabled: 'AI insights disabled',
        anti_spam_suppressed: 'Anti-spam suppressed',
    };
    return reasons.map(r => labels[r] || r).join(', ');
};

// -- Expandable Execution Row --

const ExecutionRow = ({ exec }) => {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    return (
        <>
            <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
                <TableCell sx={{ width: 40 }}>
                    <IconButton size="small">
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Typography
                        component="a"
                        href={`/analysis-tasks/${exec.taskId}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/analysis-tasks/${exec.taskId}`); }}
                        sx={{ textDecoration: 'none', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                    >
                        {exec.taskName}
                    </Typography>
                </TableCell>
                <TableCell><StatusChip status={exec.status} /></TableCell>
                <TableCell>
                    <Tooltip title={formatDateTime(exec.startedAt)}>
                        <span>{formatRelativeTime(exec.startedAt)}</span>
                    </Tooltip>
                </TableCell>
                <TableCell>{formatDuration(exec.duration)}</TableCell>
                <TableCell>{exec.tokensUsed}</TableCell>
                <TableCell>{exec.toolCalls?.length || 0}</TableCell>
                <TableCell>
                    {exec.notificationSent
                        ? <Chip size="small" color="info" label={exec.notificationType === 'AGENT_RECOMMENDATION' ? 'Recommendation' : 'Message'} />
                        : <Chip size="small" variant="outlined" label="No" />
                    }
                </TableCell>
                <TableCell>
                    {exec.status === 'SKIPPED' && exec.skipReason
                        ? <Typography variant="caption" color="text.secondary">{exec.skipReason}</Typography>
                        : '-'
                    }
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell sx={{ py: 0 }} colSpan={9}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2 }}>
                            <Grid container spacing={2}>
                                {exec.aiResponse && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" gutterBottom>AI Response</Typography>
                                        <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 200, overflow: 'auto', bgcolor: 'grey.50' }}>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{exec.aiResponse}</Typography>
                                        </Paper>
                                    </Grid>
                                )}
                                {exec.toolCalls?.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" gutterBottom>Tool Calls ({exec.toolCalls.length})</Typography>
                                        {exec.toolCalls.map((tc, i) => (
                                            <Paper key={i} variant="outlined" sx={{ p: 1, mb: 0.5, bgcolor: 'grey.50' }}>
                                                <Typography variant="caption" fontWeight="bold">{tc.name}</Typography>
                                                <Typography variant="caption" component="pre" sx={{ maxHeight: 100, overflow: 'auto', display: 'block' }}>
                                                    {JSON.stringify(tc.args, null, 2)}
                                                </Typography>
                                            </Paper>
                                        ))}
                                    </Grid>
                                )}
                                {exec.proposedActions?.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" gutterBottom>Proposed Actions ({exec.proposedActions.length})</Typography>
                                        {exec.proposedActions.map((pa, i) => (
                                            <Chip key={i} size="small" label={pa.name || pa.tool || JSON.stringify(pa)} sx={{ mr: 0.5, mb: 0.5 }} />
                                        ))}
                                    </Grid>
                                )}
                                {exec.error && (
                                    <Grid item xs={12}>
                                        <Alert severity="error" variant="outlined">{exec.error}</Alert>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                        Execution ID: {exec.executionId}
                                        {exec.notificationId && ` | Notification ID: ${exec.notificationId}`}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

// -- Main Component --

const UserAIAnalystTab = ({ uid }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [status, setStatus] = useState(null);
    const [executions, setExecutions] = useState({ executions: [], total: 0 });
    const [notifications, setNotifications] = useState({ notifications: [], total: 0, summary: {} });

    // Execution filters
    const [execPage, setExecPage] = useState(0);
    const [execTaskFilter, setExecTaskFilter] = useState('');
    const [execStatusFilter, setExecStatusFilter] = useState('');

    // Notification pagination
    const [notifPage, setNotifPage] = useState(0);

    // Test execution state
    const [testingTaskId, setTestingTaskId] = useState(null);
    const [testResult, setTestResult] = useState(null);

    const PAGE_SIZE = 20;

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [statusData, execData, notifData] = await Promise.all([
                adminApi.getUserAIStatus(uid),
                adminApi.getUserAIExecutions(uid, {
                    limit: PAGE_SIZE,
                    offset: execPage * PAGE_SIZE,
                    ...(execTaskFilter ? { taskId: execTaskFilter } : {}),
                    ...(execStatusFilter ? { status: execStatusFilter } : {}),
                }),
                adminApi.getUserAINotifications(uid, {
                    limit: PAGE_SIZE,
                    offset: notifPage * PAGE_SIZE,
                }),
            ]);
            setStatus(statusData);
            setExecutions(execData);
            setNotifications(notifData);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setError(errorInfo.message);
        } finally {
            setLoading(false);
        }
    }, [uid, execPage, execTaskFilter, execStatusFilter, notifPage]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleRunTask = useCallback(async (taskId) => {
        try {
            setTestingTaskId(taskId);
            setTestResult(null);
            const result = await adminApi.testAnalysisTask(taskId, uid);
            setTestResult({ taskId, ...result });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setTestResult({ taskId, error: errorInfo.message });
        } finally {
            setTestingTaskId(null);
        }
    }, [uid]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            {/* ===== AC4: STATUS PANEL ===== */}
            <Typography variant="h6" gutterBottom>AI Analysis Status</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Notification Preferences */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Notification Preference</Typography>
                            <Chip
                                icon={status?.notificationPreferences?.agentEnabled ? <Notifications /> : <Block />}
                                label={status?.notificationPreferences?.agentEnabled ? 'AI Insights Enabled' : 'AI Insights Disabled'}
                                color={status?.notificationPreferences?.agentEnabled ? 'success' : 'error'}
                                size="small"
                            />
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }} color="text.secondary">
                                Source: {status?.notificationPreferences?.source || 'default'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Subscription AI Limits */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>AI Limits (Today)</Typography>
                            <Typography variant="body2">
                                Runs: {status?.subscriptionLimits?.usageToday?.analysisRuns || 0} / {status?.subscriptionLimits?.aiInsightsLimit || 'Unlimited'}
                            </Typography>
                            <Typography variant="body2">
                                Emails: {status?.subscriptionLimits?.usageToday?.emailsSent || 0} / {status?.subscriptionLimits?.aiInsightsEmailLimit || 'Unlimited'}
                            </Typography>
                            {status?.subscriptionLimits?.aiInsightsLimit > 0 && (
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(((status.subscriptionLimits.usageToday?.analysisRuns || 0) / status.subscriptionLimits.aiInsightsLimit) * 100, 100)}
                                    sx={{ mt: 1 }}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Anti-Spam State */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Anti-Spam</Typography>
                            <Typography variant="body2">
                                Emails today: {status?.antiSpam?.dailyEmailCount || 0} / {status?.antiSpam?.dailyEmailLimit || 3}
                            </Typography>
                            <Typography variant="body2">
                                Notifications today: {status?.antiSpam?.dailyNotifCount || 0} / {status?.antiSpam?.dailyNotifLimit || 5}
                            </Typography>
                            {status?.antiSpam?.perTaskIgnored?.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    {status.antiSpam.perTaskIgnored.map((item, i) => (
                                        <Chip
                                            key={i}
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                            label={`${item.taskName}: ${item.ignoredCount}/${item.maxIgnored} ignored`}
                                            sx={{ mb: 0.5, mr: 0.5 }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Quick Stats */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Quick Stats</Typography>
                            <Typography variant="body2">Total: {status?.stats?.totalExecutions || 0}</Typography>
                            <Typography variant="body2">Last 24h: {status?.stats?.executionsLast24h || 0}</Typography>
                            <Typography variant="body2">Last 7d: {status?.stats?.executionsLast7d || 0}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Last run: {status?.stats?.lastExecutionAt ? formatRelativeTime(status.stats.lastExecutionAt) : 'Never'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* ===== AC2: EXECUTION HISTORY ===== */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 3 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>Execution History</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Task</InputLabel>
                        <Select
                            value={execTaskFilter}
                            label="Task"
                            onChange={(e) => { setExecTaskFilter(e.target.value); setExecPage(0); }}
                        >
                            <MenuItem value="">All Tasks</MenuItem>
                            {status?.taskEligibility?.map(t => (
                                <MenuItem key={t.taskId} value={t.taskId}>{t.taskName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={execStatusFilter}
                            label="Status"
                            onChange={(e) => { setExecStatusFilter(e.target.value); setExecPage(0); }}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="COMPLETED">Completed</MenuItem>
                            <MenuItem value="FAILED">Failed</MenuItem>
                            <MenuItem value="SKIPPED">Skipped</MenuItem>
                        </Select>
                    </FormControl>
                    <Button startIcon={<Refresh />} size="small" onClick={loadData}>Refresh</Button>
                </Stack>
            </Box>

            {executions.executions.length === 0 ? (
                <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
                    No AI analysis executions found for this user.
                </Alert>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 40 }} />
                                <TableCell>Task Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Started At</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell>Tokens</TableCell>
                                <TableCell>Tools</TableCell>
                                <TableCell>Notification</TableCell>
                                <TableCell>Skip Reason</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {executions.executions.map((exec) => (
                                <ExecutionRow key={exec.executionId} exec={exec} />
                            ))}
                        </TableBody>
                    </Table>
                    {/* Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Showing {execPage * PAGE_SIZE + 1}-{Math.min((execPage + 1) * PAGE_SIZE, executions.total)} of {executions.total}
                        </Typography>
                        <Box>
                            <Button size="small" disabled={execPage === 0} onClick={() => setExecPage(p => p - 1)}>Previous</Button>
                            <Button size="small" disabled={(execPage + 1) * PAGE_SIZE >= executions.total} onClick={() => setExecPage(p => p + 1)}>Next</Button>
                        </Box>
                    </Box>
                </TableContainer>
            )}

            {/* ===== AC3: NOTIFICATION FEED ===== */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>AI Notifications</Typography>

            {/* Summary Stats */}
            {notifications.summary && notifications.total > 0 && (
                <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item>
                        <Chip size="small" label={`Total: ${notifications.summary.totalSent || 0}`} />
                    </Grid>
                    <Grid item>
                        <Chip size="small" color="info" label={`Read: ${Math.round((notifications.summary.readRate || 0) * 100)}%`} />
                    </Grid>
                    <Grid item>
                        <Chip size="small" color="success" icon={<ThumbUp />} label={`${notifications.summary.feedbackPositive || 0}`} />
                    </Grid>
                    <Grid item>
                        <Chip size="small" color="error" icon={<ThumbDown />} label={`${notifications.summary.feedbackNegative || 0}`} />
                    </Grid>
                    <Grid item>
                        <Chip size="small" variant="outlined" label={`Dismissed: ${Math.round((notifications.summary.dismissalRate || 0) * 100)}%`} />
                    </Grid>
                </Grid>
            )}

            {notifications.notifications.length === 0 ? (
                <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
                    No AI notifications have been delivered to this user.
                </Alert>
            ) : (
                <Paper variant="outlined" sx={{ mb: 3 }}>
                    <List disablePadding>
                        {notifications.notifications.map((notif, idx) => (
                            <React.Fragment key={notif.id}>
                                {idx > 0 && <Divider />}
                                <ListItem alignItems="flex-start">
                                    <ListItemIcon sx={{ minWidth: 40, mt: 1 }}>
                                        {notif.type === 'AGENT_RECOMMENDATION' ? <Recommend color="primary" /> : <Message color="action" />}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle2">{notif.title}</Typography>
                                                {notif.read
                                                    ? <Tooltip title="Read"><MarkEmailRead fontSize="small" color="success" /></Tooltip>
                                                    : <Tooltip title="Unread"><MarkEmailUnread fontSize="small" color="warning" /></Tooltip>
                                                }
                                                {notif.channels.includes('email') && (
                                                    <Tooltip title={notif.emailSentAt ? `Email sent ${formatDateTime(notif.emailSentAt)}` : 'Email sent'}>
                                                        <Email fontSize="small" color="action" />
                                                    </Tooltip>
                                                )}
                                                {notif.feedback === 'positive' && <ThumbUp fontSize="small" color="success" />}
                                                {notif.feedback === 'negative' && <ThumbDown fontSize="small" color="error" />}
                                                {notif.dismissed && <Chip size="small" variant="outlined" label="Dismissed" />}
                                            </Box>
                                        }
                                        secondary={
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" sx={{
                                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                                                }}>
                                                    {notif.message}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {notif.taskName && (
                                                        <Typography
                                                            component="a"
                                                            variant="caption"
                                                            href={`/analysis-tasks/${notif.taskId}`}
                                                            onClick={(e) => { e.preventDefault(); navigate(`/analysis-tasks/${notif.taskId}`); }}
                                                            sx={{ textDecoration: 'none', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                                                        >
                                                            {notif.taskName}
                                                        </Typography>
                                                    )}
                                                    {' '}
                                                    <Tooltip title={formatDateTime(notif.createdAt)}>
                                                        <span>{formatRelativeTime(notif.createdAt)}</span>
                                                    </Tooltip>
                                                    {notif.recommendationCount > 0 && ` | ${notif.recommendationCount} recommendations`}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            </React.Fragment>
                        ))}
                    </List>
                    {/* Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">
                            Showing {notifPage * PAGE_SIZE + 1}-{Math.min((notifPage + 1) * PAGE_SIZE, notifications.total)} of {notifications.total}
                        </Typography>
                        <Box>
                            <Button size="small" disabled={notifPage === 0} onClick={() => setNotifPage(p => p - 1)}>Previous</Button>
                            <Button size="small" disabled={(notifPage + 1) * PAGE_SIZE >= notifications.total} onClick={() => setNotifPage(p => p + 1)}>Next</Button>
                        </Box>
                    </Box>
                </Paper>
            )}

            {/* ===== AC5: TASK ELIGIBILITY ===== */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Task Eligibility</Typography>

            {(!status?.taskEligibility || status.taskEligibility.length === 0) ? (
                <Alert severity="info" variant="outlined">
                    No active analysis tasks found.
                </Alert>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Task Name</TableCell>
                                <TableCell>Eligible</TableCell>
                                <TableCell>Reason</TableCell>
                                <TableCell>Last Run</TableCell>
                                <TableCell>Next Eligible</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {status.taskEligibility.map((task) => (
                                <React.Fragment key={task.taskId}>
                                    <TableRow>
                                        <TableCell>
                                            <Typography
                                                component="a"
                                                href={`/analysis-tasks/${task.taskId}`}
                                                onClick={(e) => { e.preventDefault(); navigate(`/analysis-tasks/${task.taskId}`); }}
                                                sx={{ textDecoration: 'none', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                                            >
                                                {task.taskName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell><EligibleChip eligible={task.eligible} /></TableCell>
                                        <TableCell>
                                            {task.reasons.length > 0 ? (
                                                <Typography variant="caption" color="text.secondary">{formatReasons(task.reasons)}</Typography>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {task.lastRunAt ? (
                                                <Tooltip title={formatDateTime(task.lastRunAt)}>
                                                    <span>{formatRelativeTime(task.lastRunAt)}</span>
                                                </Tooltip>
                                            ) : 'Never'}
                                        </TableCell>
                                        <TableCell>
                                            {task.nextEligibleAt ? (
                                                <Tooltip title={formatDateTime(task.nextEligibleAt)}>
                                                    <span>{formatRelativeTime(task.nextEligibleAt)}</span>
                                                </Tooltip>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={testingTaskId === task.taskId ? <CircularProgress size={16} /> : <PlayArrow />}
                                                disabled={testingTaskId !== null}
                                                onClick={() => handleRunTask(task.taskId)}
                                            >
                                                Run Now
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    {/* Show test result inline */}
                                    {testResult && testResult.taskId === task.taskId && (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                {testResult.error ? (
                                                    <Alert severity="error" variant="outlined" sx={{ m: 1 }}>{testResult.error}</Alert>
                                                ) : (
                                                    <Paper variant="outlined" sx={{ p: 1.5, m: 1, bgcolor: 'grey.50' }}>
                                                        <Typography variant="subtitle2" gutterBottom>
                                                            Test Result: <StatusChip status={testResult.testResult?.status || 'UNKNOWN'} />
                                                        </Typography>
                                                        {testResult.testResult?.summary && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                Tokens: {testResult.testResult.summary.tokensUsed || 0} |
                                                                Tools: {testResult.testResult.summary.toolCallCount || 0} |
                                                                Notification: {testResult.testResult.summary.notificationSent ? 'Sent' : 'No'}
                                                            </Typography>
                                                        )}
                                                    </Paper>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default UserAIAnalystTab;
